import { VaultNavigationTabs } from 'common/enums';
import { DashboardScope } from 'context/vault-detail-context/context-reducer';
import { goToPage, RouteName } from 'routes';
import { findRoute } from 'routes/route-helpers';

export interface Breadcrumb {
  label: string;
  // Omitted for a crumb that stands for the current screen (not clickable).
  onClick?: () => void;
}

// Where the user navigated from. Carried in the router location state so a
// screen can show the path actually taken (Dashboard → album) instead of the
// canonical one (Dashboard → Albums → album). It is lost on reload or deep
// link, in which case the trail falls back to the canonical one.
export interface BreadcrumbOrigin {
  // The vault whose page the user came from. For a "Shared With Me" album this
  // differs from the vault in the URL, which is the album owner's.
  vaultId: number;
  tab: VaultNavigationTabs;
  // Set when the user came from the dashboard's "Shared With Me" scope so the
  // Dashboard crumb can restore it.
  scope?: DashboardScope;
}

export type BreadcrumbLocationState = { breadcrumbOrigin?: BreadcrumbOrigin };

// Location state to pass to `goToPage` so the destination knows its origin.
export const withOrigin = (
  origin: BreadcrumbOrigin | undefined,
): BreadcrumbLocationState | undefined =>
  origin ? { breadcrumbOrigin: origin } : undefined;

export const goToDashboard = ({
  vaultId,
  scope,
}: Pick<BreadcrumbOrigin, 'vaultId' | 'scope'>) =>
  goToPage(
    RouteName.VaultDetail,
    { id: vaultId },
    scope === 'shared' ? { scope } : {},
  );

export const goToVaultTab = (vaultId: number, tab: VaultNavigationTabs) =>
  goToPage(RouteName.VaultDetail, { id: vaultId }, { tab });

// The vault-page crumbs for the place the user came from: "Dashboard", plus
// the tab when it wasn't the dashboard itself. Tab enum values double as the
// labels shown in the tabs bar.
const originCrumbs = (origin: BreadcrumbOrigin): Breadcrumb[] => {
  const crumbs: Breadcrumb[] = [
    { label: 'Dashboard', onClick: () => goToDashboard(origin) },
  ];
  if (origin.tab !== VaultNavigationTabs.dashboard) {
    crumbs.push({
      label: origin.tab,
      onClick: () => goToVaultTab(origin.vaultId, origin.tab),
    });
  }
  return crumbs;
};

// /vaults/:id — the vault page. The selected tab is the current screen.
export const vaultPageBreadcrumb = (
  vaultId: number,
  tab: VaultNavigationTabs,
): Breadcrumb[] =>
  tab === VaultNavigationTabs.dashboard
    ? [{ label: 'Dashboard' }]
    : [
        { label: 'Dashboard', onClick: () => goToDashboard({ vaultId }) },
        { label: tab },
      ];

interface AlbumTrailArgs {
  vaultId: number;
  albumId: number;
  albumName: string;
  origin?: BreadcrumbOrigin;
}

const albumOrigin = (vaultId: number): BreadcrumbOrigin => ({
  vaultId,
  tab: VaultNavigationTabs.albums,
});

// /vaults/:id/album/:albumId — the single-album (grid / planner) page.
export const albumPageBreadcrumb = ({
  vaultId,
  albumName,
  origin,
}: Omit<AlbumTrailArgs, 'albumId'>): Breadcrumb[] => [
  ...originCrumbs(origin ?? albumOrigin(vaultId)),
  { label: albumName },
];

// /vaults/:id/album/:albumId/view — the memory player inside an album. The
// memory itself is not a crumb (its title is in the sub-header), so the album
// is the last crumb and stays clickable, returning to the album page.
export const albumMemoryBreadcrumb = ({
  vaultId,
  albumId,
  albumName,
  origin,
}: AlbumTrailArgs): Breadcrumb[] => [
  ...originCrumbs(origin ?? albumOrigin(vaultId)),
  {
    label: albumName,
    onClick: () =>
      goToPage(
        RouteName.AlbumScreen,
        { id: vaultId, albumId },
        {},
        withOrigin(origin),
      ),
  },
];

// /vaults/:id/memories/view — the vault-wide memory player.
export const memoriesBreadcrumb = (
  vaultId: number,
  origin?: BreadcrumbOrigin,
): Breadcrumb[] =>
  originCrumbs(origin ?? { vaultId, tab: VaultNavigationTabs.memories });

// /vaults/:id/<something> — a vault-scoped screen that isn't a tab, e.g.
// Settings or Import. Shown as "Dashboard / <label>".
export const vaultSubPageBreadcrumb = (
  vaultId: number,
  label: string,
): Breadcrumb[] => [
  { label: 'Dashboard', onClick: () => goToDashboard({ vaultId }) },
  { label },
];

// Account-level screens (outside any vault) and their crumb labels. Their
// trail is rooted at the vaults list, which is the only way back from them.
const APP_PAGE_LABELS: Partial<Record<RouteName, string>> = {
  [RouteName.Collaborators]: 'Collaborators',
  [RouteName.Store]: 'Store',
  [RouteName.Account]: 'Account',
  [RouteName.Templates]: 'Templates',
  [RouteName.VaultNew]: 'New Vault',
  [RouteName.ReviewVideos]: 'Review Videos',
};

// Account-level screens have no vault in their URL, so their "Dashboard" crumb
// targets the vault the user most recently opened. Remembered across reloads.
const LAST_VAULT_KEY = 'gs:lastVaultId';

export const rememberLastVaultId = (vaultId: number) => {
  if (!(vaultId > 0)) return;
  try {
    window.localStorage.setItem(LAST_VAULT_KEY, String(vaultId));
  } catch {
    // Storage unavailable (private mode, quota): the crumb falls back.
  }
};

export const getLastVaultId = (): number | undefined => {
  try {
    const stored = Number(window.localStorage.getItem(LAST_VAULT_KEY));
    return stored > 0 ? stored : undefined;
  } catch {
    return undefined;
  }
};

// Trail for the current account-level screen, or null when `pathname` isn't
// one (onboarding, guest links, …), so nothing is shown there. The trail is
// rooted at "Dashboard" like everywhere else; with no known vault it leads to
// the vaults list instead.
export const appPageBreadcrumb = (
  pathname: string,
  dashboardVaultId?: number,
): Breadcrumb[] | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  const dashboard: Breadcrumb = {
    label: 'Dashboard',
    onClick: () =>
      dashboardVaultId
        ? goToDashboard({ vaultId: dashboardVaultId })
        : goToPage(RouteName.Vaults),
  };
  if (path === findRoute(RouteName.Vaults).path) {
    return [dashboard, { label: 'All Vaults' }];
  }
  const match = (Object.keys(APP_PAGE_LABELS) as RouteName[]).find(
    (name) => findRoute(name).path === path,
  );
  if (!match) return null;
  return [dashboard, { label: APP_PAGE_LABELS[match]! }];
};
